import isEqual from 'lodash/isEqual';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface HistoryFacade {
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;
  clear(): void;
  size(): number;
  index(): number;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    history?: HistoryFacade;
  }
}

type Snapshot = Record<string, unknown>;

export interface HistoryPluginOptions {
  limit?: number;
}

export function historyPlugin(options: HistoryPluginOptions = {}): EditorPlugin {
  const limit = options.limit ?? 100;

  return {
    name: FabricPluginName.HISTORY,
    install(editor: FabricEditor) {
      const stack: Snapshot[] = [snapshot(editor)];
      let cursor = 0;
      let restoring = false;

      const emit = () => {
        editor.emit('history:changed', {
          canUndo: cursor > 0,
          canRedo: cursor < stack.length - 1,
          index: cursor,
          size: stack.length,
        });
      };

      const record = () => {
        if (restoring) return;
        const next = snapshot(editor);
        if (isEqual(stack[cursor], next)) return;
        // truncate forward history
        stack.length = cursor + 1;
        stack.push(next);
        if (stack.length > limit) {
          stack.shift();
        } else {
          cursor++;
        }
        emit();
      };

      const restore = (data: Snapshot, done: () => void) => {
        restoring = true;
        editor.canvas.loadFromJSON(data, () => {
          editor.canvas.renderAll();
          restoring = false;
          editor.emit('canvas:loaded', { source: 'history' });
          done();
        });
      };

      const facade: HistoryFacade = {
        undo() {
          if (cursor <= 0) return;
          cursor--;
          restore(stack[cursor], emit);
        },
        redo() {
          if (cursor >= stack.length - 1) return;
          cursor++;
          restore(stack[cursor], emit);
        },
        canUndo: () => cursor > 0,
        canRedo: () => cursor < stack.length - 1,
        clear() {
          stack.length = 0;
          stack.push(snapshot(editor));
          cursor = 0;
          emit();
        },
        size: () => stack.length,
        index: () => cursor,
      };

      editor.history = facade;

      const offAdded = editor.on('object:added', record);
      const offModified = editor.on('object:modified', record);
      const offRemoved = editor.on('object:removed', record);

      // Stash unsubscribers on the plugin closure for dispose.
      (facade as HistoryFacade & { __off?: () => void }).__off = () => {
        offAdded();
        offModified();
        offRemoved();
      };
    },
    dispose(editor: FabricEditor) {
      const facade = editor.history as (HistoryFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.history = undefined;
    },
  };
}

function snapshot(editor: FabricEditor): Snapshot {
  const json = editor.canvas.toJSON() as Record<string, unknown> & { objects?: unknown[]; version?: string };
  return { version: json.version, objects: json.objects ?? [] };
}
